/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { BEM } from "@adapptio/lib-components";
import { FieldSectionContextProvider, useFieldSectionContext } from "../../../contexts";
import { useLocalStorage } from "../../../hooks";

import "./FieldSection.scss";

export interface IFieldSectionProps {
	children?: React.ReactNode;
	className?: string;
	stateKey: string;
	collapsible?: boolean;
	defaultCollapsed?: boolean;
	upperMargin?: boolean;
	group?: boolean;
}

const FieldSection: React.FC<IFieldSectionProps> = ({
	children,
	className,
	stateKey,
	collapsible,
	defaultCollapsed,
	upperMargin,
	group
}) => {
	const fieldSectionCtx = useFieldSectionContext();

	const root = !fieldSectionCtx && group;

	const baseClass = BEM("edt-field-section", null, {
		root,
		group,
		"upper-margin": upperMargin
	});

	const [ collapsed, setCollapsed ] = useLocalStorage(
		"adpt-editor-field-section_" + stateKey,
		defaultCollapsed
	);

	const toggleCollapsed = useCallback(() => {
		setCollapsed((prevState) => !prevState);
	}, [ setCollapsed ]);

	if (className) {
		baseClass.add(className);
	}

	return (
		<div className={baseClass.toClassName()}>
			<FieldSectionContextProvider
				collapsible={collapsible}
				collapsed={collapsed}
				toggleCollapsed={toggleCollapsed}
				group={group}
			>
				{children}
			</FieldSectionContextProvider>
		</div>
	);
};

FieldSection.defaultProps = {
	defaultCollapsed: false
};

const FieldSectionMemo = React.memo(FieldSection);

export { FieldSectionMemo as FieldSection };
